import { useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * This hook handles both browser and in-app navigation warnings
 * when there are unsaved changes
 */
export const useUnsavedChangesWarning = (
    hasUnsavedChanges: boolean,
    message: string = 'You have unsaved changes. Are you sure you want to leave?'
) => {
    const navigate = useNavigate();
    const location = useLocation();

    // Track when we're handling navigation to prevent loops
    const isHandlingNavigation = useRef(false);
    const allowedNavigation = useRef(false);

    // Track the initial location
    const initialLocation = useRef(location);

    // For programmatic navigation with confirmation
    const confirmNavigate = useCallback((to: string, options?: any) => {
        if (!hasUnsavedChanges) {
            navigate(to, options);
            return;
        }

        const userConfirmed = window.confirm(message);
        if (userConfirmed) {
            allowedNavigation.current = true;
            navigate(to, options);
        }
    }, [hasUnsavedChanges, message, navigate]);

    // Handle browser refresh/close with beforeunload event
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (!hasUnsavedChanges) return;

            e.preventDefault();
            e.returnValue = message;
            return message;
        };

        if (hasUnsavedChanges) {
            window.addEventListener('beforeunload', handleBeforeUnload);
        }

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasUnsavedChanges, message]);

    // // Main handler for navigation changes - handles both hash and normal navigation
    // useEffect(() => {
    //     // Skip on initial render and if navigation is allowed
    //     if (location === initialLocation.current || allowedNavigation.current) {
    //         allowedNavigation.current = false;
    //         return;
    //     }

    //     // Handle only if we have unsaved changes and aren't already handling navigation
    //     if (hasUnsavedChanges && !isHandlingNavigation.current) {
    //         isHandlingNavigation.current = true;

    //         // Show confirmation dialog for navigation
    //         const userConfirmed = window.confirm(message);

    //         if (userConfirmed) {
    //             // If confirmed, allow the navigation
    //             allowedNavigation.current = true;
    //         } else {
    //             // If rejected, go back to the previous URL
    //             window.history.back();
    //         }

    //         // Reset the handling flag after a small delay
    //         setTimeout(() => {
    //             isHandlingNavigation.current = false;
    //         }, 100);
    //     }
    // }, [location, hasUnsavedChanges, message]);

    return { confirmNavigate };
};

export default useUnsavedChangesWarning; 